import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  CheckCircle,
  Wifi,
  Users,
  PhoneCall,
  ArrowRight,
  Headphones,
  Settings,
  Monitor,
} from "lucide-react";

const PhysicalPhonesSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

      <div className="flex flex-row items-center gap-16 max-w-7xl mx-auto z-10 w-full">
        {/* Left Content */}
        <div className="flex-1 space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200">
            <Phone className="w-4 h-4" />
            <span>Physical Phones</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
            Enterprise-Grade Phones,{" "}
            <span className="text-blue-600">Fully Integrated.</span>
          </h2>

          <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
            Yealink desk phones and other hardware devices plug directly into
            Asha's VoIP platform. Crystal-clear calls, zero configuration
            headaches.
          </p>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-slate-900 mb-1">
                Yealink
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Certified Partner
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-slate-900 mb-1">
                Plug & Play
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Zero IT Setup
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                <Wifi className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">
                  Native VoIP Integration
                </h4>
                <p className="text-slate-500 text-sm">
                  Phones connect directly to Asha's cloud PBX — patient pop-ups,
                  call routing, and AI all built in.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 flex-shrink-0">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">
                  Hot Desking & Multi-Line
                </h4>
                <p className="text-slate-500 text-sm">
                  Staff log in to any phone. Multiple lines, call parking, and
                  intercom across your whole office.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                <Headphones className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">
                  HD Voice & Reliability
                </h4>
                <p className="text-slate-500 text-sm">
                  Crystal-clear audio with 99.99% uptime. No more dropped calls
                  or choppy connections.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Visual - Physical Phone Mockup */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex-1 w-full max-w-md"
        >
          <div className="relative">
            {/* Phone Device */}
            <div className="bg-slate-800 rounded-3xl p-6 shadow-2xl border border-slate-700">
              {/* Phone Screen */}
              <div className="bg-slate-900 rounded-2xl overflow-hidden">
                {/* Screen Header */}
                <div className="bg-blue-600 px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-white text-xs font-semibold">
                      Connected to Asha VoIP
                    </span>
                  </div>
                  <Settings className="w-3.5 h-3.5 text-white/60" />
                </div>

                {/* Active Call Display */}
                <div className="p-5 space-y-4">
                  <div className="text-center space-y-2">
                    <motion.div
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-blue-500/30"
                    >
                      JD
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-lg">
                        James Davis
                      </p>
                      <p className="text-blue-400 text-sm animate-pulse">
                        Active Call · 2:34
                      </p>
                    </div>
                  </div>

                  {/* Patient Info Pop-up */}
                  <motion.div
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10 space-y-2"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Monitor className="w-3 h-3 text-blue-400" />
                      <span className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">
                        Patient Info
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-slate-800/60 p-2 rounded-lg">
                        <span className="text-[9px] text-slate-400 block">
                          Next Appt
                        </span>
                        <span className="text-xs font-semibold text-white">
                          Thu, 10:00 AM
                        </span>
                      </div>
                      <div className="bg-slate-800/60 p-2 rounded-lg">
                        <span className="text-[9px] text-slate-400 block">
                          Balance
                        </span>
                        <span className="text-xs font-semibold text-emerald-400">
                          $0.00
                        </span>
                      </div>
                      <div className="bg-slate-800/60 p-2 rounded-lg col-span-2">
                        <span className="text-[9px] text-slate-400 block">
                          Insurance
                        </span>
                        <span className="text-xs font-semibold text-white">
                          Delta Dental PPO — Active
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Call Actions */}
                  <div className="grid grid-cols-4 gap-2 pt-2">
                    {[
                      { icon: PhoneCall, label: "Transfer", color: "blue" },
                      { icon: Users, label: "Conference", color: "indigo" },
                      { icon: Phone, label: "Hold", color: "amber" },
                      { icon: Headphones, label: "Record", color: "red" },
                    ].map((action, i) => (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <div
                          className={`w-10 h-10 rounded-full bg-${action.color}-500/20 flex items-center justify-center text-${action.color}-400`}
                        >
                          <action.icon className="w-4 h-4" />
                        </div>
                        <span className="text-[9px] text-slate-400 font-medium">
                          {action.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Physical Keys */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, "*", 0, "#"].map((key, i) => (
                  <div
                    key={i}
                    className="h-8 bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 text-xs font-semibold hover:bg-slate-600 transition-colors"
                  >
                    {key}
                  </div>
                ))}
              </div>
            </div>

            {/* Floating Labels */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute -right-4 top-8 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-2 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
              </div>
              <span className="text-xs font-bold text-slate-700">
                Yealink T54W
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -left-4 bottom-24 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-2 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <Wifi className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <span className="text-xs font-bold text-slate-700">
                Cloud PBX
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PhysicalPhonesSlide;
